import { useEffect, useMemo, useState } from 'react';
import { FiCheck, FiGlobe, FiSave } from 'react-icons/fi';
import { toast } from 'react-toastify';
import {
  useGetBillingTaxProfileQuery,
  useUpdateBillingTaxProfileMutation,
} from '@/store/api/apiSlice';
import {
  BILLING_COUNTRY_OPTIONS,
  taxTypesForCountry,
  type BillingTaxProfile,
  type BillingTaxType,
} from '@/config/billingTax';
import { getApiError } from '@/utils/errorUtils';

const emptyForm: BillingTaxProfile = {
  countryCode: 'US',
  taxType: 'none',
  legalName: '',
  taxId: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  stateRegion: '',
  postalCode: '',
};

export default function BillingTaxSetupPanel({
  billingConfigured = false,
}: {
  billingConfigured?: boolean;
}) {
  const providerActive = billingConfigured;
  const { data, isLoading } = useGetBillingTaxProfileQuery(undefined);
  const [updateProfile, { isLoading: saving }] = useUpdateBillingTaxProfileMutation();
  const [form, setForm] = useState<BillingTaxProfile>(emptyForm);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data?.profile) {
      setForm({ ...emptyForm, ...data.profile });
      setDirty(false);
    }
  }, [data?.profile]);

  const taxOptions = useMemo(
    () => taxTypesForCountry(form.countryCode),
    [form.countryCode],
  );

  const selectedTaxHint =
    taxOptions.find((t) => t.id === form.taxType)?.hint ?? 'Select your tax registration type';

  const setField = <K extends keyof BillingTaxProfile>(key: K, value: BillingTaxProfile[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleCountryChange = (countryCode: string) => {
    const options = taxTypesForCountry(countryCode);
    const nextType = options.some((o) => o.id === form.taxType)
      ? form.taxType
      : ('none' as BillingTaxType);
    setForm((prev) => ({
      ...prev,
      countryCode,
      taxType: nextType,
      taxId: nextType === 'none' ? '' : prev.taxId,
    }));
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      await updateProfile({
        legalName: form.legalName?.trim() || undefined,
        countryCode: form.countryCode,
        taxType: form.taxType,
        taxId: form.taxType === 'none' ? undefined : form.taxId?.trim(),
        addressLine1: form.addressLine1?.trim() || undefined,
        addressLine2: form.addressLine2?.trim() || undefined,
        city: form.city?.trim() || undefined,
        stateRegion: form.stateRegion?.trim() || undefined,
        postalCode: form.postalCode?.trim() || undefined,
      }).unwrap();
      toast.success('Tax profile saved — future invoices will use this information');
      setDirty(false);
    } catch (err) {
      toast.error(getApiError(err, 'Could not save tax profile'));
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <FiGlobe className="w-4 h-4 text-indigo-600" />
            Invoice &amp; tax setup
          </h2>
          <p className="text-[11px] text-slate-500 mt-1 max-w-2xl leading-relaxed">
            Add your legal name, billing address, and tax ID (GST, VAT, etc.).
            {providerActive
              ? ' Saved on your organization for GST/VAT invoices.'
              : ' Saved on your organization — will sync when billing is enabled.'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving || !dirty || isLoading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          <FiSave className="w-3.5 h-3.5" />
          {saving ? 'Saving…' : 'Save tax profile'}
        </button>
      </div>

      {isLoading ? (
        <p className="text-xs text-slate-500 py-4">Loading tax profile…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block md:col-span-2">
            <span className="text-[11px] font-medium text-slate-600">Legal / company name</span>
            <input
              type="text"
              value={form.legalName ?? ''}
              onChange={(e) => setField('legalName', e.target.value)}
              placeholder="Acme Pty Ltd"
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </label>

          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Country</span>
            <select
              value={form.countryCode}
              onChange={(e) => handleCountryChange(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
            >
              {BILLING_COUNTRY_OPTIONS.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Tax type</span>
            <select
              value={form.taxType}
              onChange={(e) => {
                const taxType = e.target.value as BillingTaxType;
                setField('taxType', taxType);
                if (taxType === 'none') setField('taxId', '');
              }}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
            >
              {taxOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-slate-400 mt-1">{selectedTaxHint}</p>
          </label>

          {form.taxType !== 'none' && (
            <label className="block md:col-span-2">
              <span className="text-[11px] font-medium text-slate-600">Tax ID number</span>
              <input
                type="text"
                value={form.taxId ?? ''}
                onChange={(e) => setField('taxId', e.target.value)}
                placeholder="e.g. 22AAAAA0000A1Z5 (GSTIN)"
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </label>
          )}

          <label className="block md:col-span-2">
            <span className="text-[11px] font-medium text-slate-600">Address line 1</span>
            <input
              type="text"
              value={form.addressLine1 ?? ''}
              onChange={(e) => setField('addressLine1', e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </label>

          <label className="block md:col-span-2">
            <span className="text-[11px] font-medium text-slate-600">Address line 2</span>
            <input
              type="text"
              value={form.addressLine2 ?? ''}
              onChange={(e) => setField('addressLine2', e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </label>

          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">City</span>
            <input
              type="text"
              value={form.city ?? ''}
              onChange={(e) => setField('city', e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </label>

          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">State / region</span>
            <input
              type="text"
              value={form.stateRegion ?? ''}
              onChange={(e) => setField('stateRegion', e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </label>

          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Postal code</span>
            <input
              type="text"
              value={form.postalCode ?? ''}
              onChange={(e) => setField('postalCode', e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </label>
        </div>
      )}

      {data?.profile?.updatedAt && !dirty && (
        <p className="text-[10px] text-slate-400 flex items-center gap-1">
          <FiCheck className="w-3 h-3 text-green-600" />
          Last saved {new Date(data.profile.updatedAt).toLocaleString()}
        </p>
      )}
    </div>
  );
}
